import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { contacts, initials, matches, NUMBER_LABELS, useContacts, type Contact, type ContactNumber, type Entry } from '../lib/contacts';
import { displayNumber, toE164AU } from '../lib/phone';
import { CloseIcon, PhoneIcon } from './Icons';

// ---------- context: any screen can open the editor / detail ----------

export interface EditorPrefill {
  number?: string;
  name?: string;
  edit?: Contact;
}

interface SheetsApi {
  openEditor: (p: EditorPrefill) => void;
  openDetail: (e: Entry) => void;
}

const SheetsContext = createContext<SheetsApi>({ openEditor: () => undefined, openDetail: () => undefined });
export const useSheets = () => useContext(SheetsContext);

export function SheetsProvider({ children, onCall }: { children: React.ReactNode; onCall: (n: string) => void }) {
  const [editor, setEditor] = useState<EditorPrefill | null>(null);
  const [detail, setDetail] = useState<Entry | null>(null);
  const api = useMemo<SheetsApi>(() => ({ openEditor: (p) => (setDetail(null), setEditor(p)), openDetail: setDetail }), []);
  return (
    <SheetsContext.Provider value={api}>
      {children}
      {editor && <ContactEditor prefill={editor} onClose={() => setEditor(null)} />}
      {detail && (
        <ContactDetail
          entry={detail}
          onClose={() => setDetail(null)}
          onCall={(n) => {
            setDetail(null);
            onCall(n);
          }}
          onEdit={(c) => api.openEditor({ edit: c })}
        />
      )}
    </SheetsContext.Provider>
  );
}

// ---------- shared bits ----------

function Sheet({ title, onClose, children, testid }: { title: string; onClose: () => void; children: React.ReactNode; testid?: string }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  return (
    <div className="sheet-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} data-testid={testid}>
        <div className="sheet-head">
          <h2>{title}</h2>
          <button type="button" className="close-btn" aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function NgnBadge() {
  return <span className="ngn-badge">NGN Customer</span>;
}

// ---------- editor ----------

function ContactEditor({ prefill, onClose }: { prefill: EditorPrefill; onClose: () => void }) {
  const { entries } = useContacts();
  const edit = prefill.edit;
  const [name, setName] = useState(edit?.name ?? prefill.name ?? '');
  const [company, setCompany] = useState(edit?.company ?? '');
  const [numbers, setNumbers] = useState<ContactNumber[]>(edit?.numbers ?? [{ label: 'mobile', number: prefill.number ?? '' }]);
  const [scope, setScope] = useState<'personal' | 'shared'>(edit?.scope ?? 'personal');
  const [target, setTarget] = useState<Contact | null>(null); // "add to existing"
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState(false);
  const [active, setActive] = useState(0);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => nameRef.current?.focus(), []);

  // Combobox: existing *editable* contacts matching the typed name — pick one to add this number to it.
  const suggestions = useMemo(() => {
    if (edit || name.trim().length < 2 || target) return [];
    return entries.filter((e): e is Extract<Entry, { contact: Contact }> => e.kind !== 'customer' && matches(e, name)).slice(0, 5);
  }, [entries, name, edit, target]);

  const save = async () => {
    setError('');
    const nums = numbers.map((n) => ({ ...n, number: n.number.trim() })).filter((n) => n.number);
    if (!target && !name.trim()) return setError('Enter a name');
    if (nums.length === 0) return setError('Enter a number');
    setBusy(true);
    try {
      if (target) for (const n of nums) await contacts.addNumber(target, { label: n.label, number: toE164AU(n.number) });
      else if (edit) await contacts.update(edit.id, { name: name.trim(), company: company.trim(), numbers: nums });
      else await contacts.create({ scope, name: name.trim(), company: company.trim(), numbers: nums });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={edit ? 'Edit Contact' : target ? `Add to ${target.name}` : 'New Contact'} onClose={onClose} testid="contact-editor">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {!edit && !target && (
          <div className="segmented" role="tablist" style={{ margin: '8px 16px 14px' }}>
            {(['personal', 'shared'] as const).map((s) => (
              <button key={s} type="button" role="tab" aria-selected={scope === s} className={scope === s ? 'on' : ''} onClick={() => setScope(s)} data-testid={`scope-${s}`}>
                {s === 'personal' ? 'My Contacts' : 'Shared'}
              </button>
            ))}
          </div>
        )}

        {target ? (
          <ul className="group">
            <li className="row plain form-row">
              <span>{target.name}</span>
              <button type="button" className="link" onClick={() => setTarget(null)}>
                Change
              </button>
            </li>
          </ul>
        ) : (
          <>
            <ul className="group">
              <li className="row plain form-row">
                <span>Name</span>
                <input
                  ref={nameRef}
                  className="plain"
                  value={name}
                  placeholder="Full name"
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={suggestions.length > 0 && focus}
                  data-testid="contact-name"
                  onFocus={() => setFocus(true)}
                  onBlur={() => window.setTimeout(() => setFocus(false), 150)}
                  onChange={(e) => {
                    setName(e.target.value);
                    setActive(0);
                  }}
                  onKeyDown={(e) => {
                    if (!suggestions.length || !focus) return;
                    if (e.key === 'ArrowDown') (e.preventDefault(), setActive((a) => Math.min(a + 1, suggestions.length - 1)));
                    if (e.key === 'ArrowUp') (e.preventDefault(), setActive((a) => Math.max(a - 1, 0)));
                  }}
                />
              </li>
              <li className="row plain form-row">
                <span>Company</span>
                <input className="plain" value={company} placeholder="Optional" onChange={(e) => setCompany(e.target.value)} data-testid="contact-company" />
              </li>
            </ul>
            {suggestions.length > 0 && focus && (
              <ul className="suggestions" role="listbox" aria-label="Add to an existing contact">
                {suggestions.map((s, i) => (
                  <li key={s.id}>
                    <button type="button" className={i === active ? 'active' : ''} onMouseDown={(e) => e.preventDefault()} onClick={() => setTarget(s.contact)}>
                      <span>Add to {s.name}</span>
                      <span className="row-sub">{s.kind === 'shared' ? 'Shared' : 'My Contacts'}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        <h3 className="section-title">Phone</h3>
        <ul className="group">
          {numbers.map((n, i) => (
            <li key={i} className="row plain form-row">
              <select
                aria-label="Label"
                value={n.label}
                style={{ flex: '0 0 auto', color: 'var(--accent)', textAlign: 'left' }}
                onChange={(e) => setNumbers((ns) => ns.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
              >
                {NUMBER_LABELS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
              <input
                className="plain"
                inputMode="tel"
                value={n.number}
                placeholder="Number"
                data-testid="contact-number"
                onChange={(e) => setNumbers((ns) => ns.map((x, j) => (j === i ? { ...x, number: e.target.value } : x)))}
              />
            </li>
          ))}
          <li className="row plain">
            <button type="button" className="link" onClick={() => setNumbers((ns) => [...ns, { label: 'work', number: '' }])}>
              + add phone
            </button>
          </li>
        </ul>

        {error && <p className="error center" style={{ marginTop: 12 }}>{error}</p>}
        <div className="row-buttons" style={{ margin: '20px 16px 4px' }}>
          <button type="submit" className="btn primary" disabled={busy} data-testid="contact-save">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

// ---------- detail ----------

function ContactDetail({ entry, onClose, onCall, onEdit }: { entry: Entry; onClose: () => void; onCall: (n: string) => void; onEdit: (c: Contact) => void }) {
  const [confirming, setConfirming] = useState(false);
  const isCustomer = entry.kind === 'customer';
  return (
    <Sheet title={isCustomer ? 'NGN Customer' : entry.kind === 'shared' ? 'Shared Contact' : 'Contact'} onClose={onClose} testid="contact-detail">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '8px 20px 18px', textAlign: 'center' }}>
        <div className={`row-avatar ${isCustomer ? 'ngn' : ''}`} style={{ width: 72, height: 72, fontSize: 26 }}>
          {initials(entry.name)}
        </div>
        <h2 style={{ margin: '6px 0 0', font: '600 22px/1.2 var(--font-display)' }}>{entry.name}</h2>
        {entry.company && <p className="row-sub" style={{ margin: 0, whiteSpace: 'normal' }}>{entry.company}</p>}
        {isCustomer && <NgnBadge />}
      </div>
      <ul className="group">
        {entry.numbers.length === 0 && <li className="row plain row-sub">No phone numbers</li>}
        {entry.numbers.map((n) => (
          <li key={n.number} className="row plain interactive">
            <button type="button" className="row-main" onClick={() => onCall(n.number)}>
              <span className="row-sub" style={{ fontSize: 13 }}>{n.label}</span>
              <span style={{ color: 'var(--accent)', fontSize: 17 }}>{displayNumber(n.number)}</span>
            </button>
            <button type="button" className="icon-btn" aria-label={`Call ${n.number}`} onClick={() => onCall(n.number)}>
              <PhoneIcon width={18} height={18} />
            </button>
          </li>
        ))}
        {isCustomer && entry.email && (
          <li className="row plain">
            <div className="row-main">
              <span className="row-sub" style={{ fontSize: 13 }}>email</span>
              <a href={`mailto:${entry.email}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontSize: 17 }}>
                {entry.email}
              </a>
            </div>
          </li>
        )}
      </ul>
      {isCustomer ? (
        <p className="section-foot">Synced from Xero via billing. Read-only — change it in Xero.</p>
      ) : (
        <ul className="group" style={{ marginTop: 20 }}>
          <li>
            <button type="button" className="row plain" onClick={() => onEdit(entry.contact)} data-testid="contact-edit">
              Edit Contact
            </button>
          </li>
          <li className="row plain" style={{ padding: 0 }}>
            <button
              type="button"
              className="row plain danger"
              data-testid="contact-delete"
              onClick={async () => {
                if (!confirming) return setConfirming(true);
                await contacts.remove(entry.id);
                onClose();
              }}
            >
              {confirming ? 'Tap again to delete' : 'Delete Contact'}
            </button>
          </li>
        </ul>
      )}
    </Sheet>
  );
}
